// Minimal WebRTC broadcast/watch helpers using WebSocket signaling.
// Broadcaster: creates one RTCPeerConnection per viewer.
// Viewer: single RTCPeerConnection to broadcaster.

const ICE = {
  iceServers: [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
    { urls: 'stun:openrelay.metered.ca:80' },
    { urls: 'turn:openrelay.metered.ca:80', username: 'openrelayproject', credential: 'openrelayproject' },
    { urls: 'turn:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' },
    { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
  ],
};

export function signalingUrl(liveId, role, peerId, token) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const wsBase = base.replace(/^http/, 'ws');
  const params = new URLSearchParams({ role, peer_id: peerId });
  if (role === 'broadcaster' && token) params.set('token', token);
  return `${wsBase}/api/ws/live/${liveId}?${params.toString()}`;
}

async function broadcasterToken() {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const stored = localStorage.getItem('ssl_token') || '';
  try {
    const r = await fetch(`${base}/api/auth/ws-token`, { credentials: 'include', headers: stored ? { Authorization: `Bearer ${stored}` } : {} });
    if (r.ok) return (await r.json()).token;
  } catch {}
  return stored;
}

export function newPeerId() {
  return 'p_' + Math.random().toString(36).slice(2, 10);
}

export class Broadcaster {
  constructor(liveId, stream, onStatus, onEvent) {
    this.liveId = liveId;
    this.stream = stream;
    this.onStatus = onStatus || (() => {});
    this.onEvent = onEvent || (() => {});
    this.peerId = newPeerId();
    this.viewers = new Map(); // viewerId -> pc
    this.ws = null;
    this.stopped = false;
  }

  connect() {
    return new Promise(async (resolve, reject) => {
      const token = await broadcasterToken();
      if (!token) { this.onStatus('unauthorized'); reject(new Error('no-token')); return; }
      const ws = new WebSocket(signalingUrl(this.liveId, 'broadcaster', this.peerId, token));
      this.ws = ws;
      let opened = false;
      ws.onopen = () => { opened = true; this.onStatus('connected'); this._startPing(); resolve(); };
      ws.onerror = () => { if (!opened) reject(new Error('ws')); };
      ws.onmessage = (e) => this._handle(JSON.parse(e.data));
      ws.onclose = (ev) => {
        this._stopPing();
        if (this.stopped) return;
        if (ev.code === 4409) { this.onStatus('replaced'); this.onEvent({ type: 'replaced' }); return; }
        if (ev.code === 4401) { this.onStatus('unauthorized'); this.onEvent({ type: 'unauthorized' }); return; }
        if (ev.code === 4404) { this.onStatus('ended'); this.onEvent({ type: 'ended' }); return; }
        this.onStatus('reconnecting');
        setTimeout(() => { if (!this.stopped) this.connect().catch(() => {}); }, 2000);
      };
    });
  }

  _startPing() { this._stopPing(); this._ping = setInterval(() => this._send({ type: 'ping' }), 20000); }
  _stopPing() { if (this._ping) { clearInterval(this._ping); this._ping = null; } }

  async _handle(msg) {
    const { type } = msg;
    const from = msg.from || msg.peer_id;
    if (type === 'heart') { this.onEvent(msg); return; }
    if (type === 'pong') return;
    if (type === 'replaced') { this.onEvent(msg); return; }
    if (type === 'welcome') {
      for (const v of msg.viewers || []) await this._createOfferFor(v);
      this.onStatus('live');
    } else if (type === 'viewer-join') {
      await this._createOfferFor(from);
    } else if (type === 'viewer-leave') {
      const pc = this.viewers.get(from);
      if (pc) { pc.close(); this.viewers.delete(from); }
    } else if (type === 'answer' && this.viewers.has(from)) {
      await this.viewers.get(from).setRemoteDescription({ type: 'answer', sdp: msg.sdp });
    } else if (type === 'ice' && this.viewers.has(from) && msg.candidate) {
      try { await this.viewers.get(from).addIceCandidate(msg.candidate); } catch {}
    }
  }

  async _createOfferFor(viewerId) {
    const old = this.viewers.get(viewerId);
    if (old) { try { old.close(); } catch {} }
    const pc = new RTCPeerConnection(ICE);
    this.viewers.set(viewerId, pc);
    this.stream.getTracks().forEach((t) => pc.addTrack(t, this.stream));
    pc.onicecandidate = (e) => {
      if (e.candidate) this._send({ type: 'ice', to: viewerId, candidate: e.candidate });
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') { try { pc.restartIce(); } catch {} }
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this._send({ type: 'offer', to: viewerId, sdp: offer.sdp });
  }

  _send(m) { try { this.ws.send(JSON.stringify(m)); } catch {} }

  stop() {
    this.stopped = true;
    this._stopPing();
    this.viewers.forEach((pc) => pc.close());
    this.viewers.clear();
    try { this.ws?.close(); } catch {}
    this.stream?.getTracks().forEach((t) => t.stop());
  }
}

export class Viewer {
  constructor(liveId, onTrack, onStatus, onEvent) {
    this.liveId = liveId;
    this.onTrack = onTrack;
    this.onStatus = onStatus || (() => {});
    this.onEvent = onEvent || (() => {});
    this.peerId = newPeerId();
    this.pc = null;
    this.broadcasterId = null;
    this.ws = null;
    this.stopped = false;
  }

  sendHeart() { this._send({ type: 'heart' }); }

  connect() {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(signalingUrl(this.liveId, 'viewer', this.peerId));
      this.ws = ws;
      let opened = false;
      ws.onopen = () => { opened = true; this._startPing(); resolve(); };
      ws.onerror = () => { if (!opened) reject(new Error('ws')); };
      ws.onmessage = (e) => this._handle(JSON.parse(e.data));
      ws.onclose = () => {
        this._stopPing();
        if (this.stopped) return;
        if (this.pc) { try { this.pc.close(); } catch {} this.pc = null; }
        this.onStatus('reconnecting');
        setTimeout(() => { if (!this.stopped) this.connect().catch(() => {}); }, 2000);
      };
    });
  }

  async _handle(msg) {
    const { type, from } = msg;
    if (type === 'heart') { this.onEvent(msg); return; }
    if (type === 'pong') return;
    if (type === 'broadcaster-left') {
      if (this.pc) { try { this.pc.close(); } catch {} this.pc = null; }
      this.broadcasterId = null;
      this.onStatus('no_broadcaster');
      return;
    }
    if (type === 'welcome') {
      this.broadcasterId = msg.broadcaster;
      this.onStatus(this.broadcasterId ? 'waiting_offer' : 'no_broadcaster');
    } else if (type === 'offer' && from) {
      this.broadcasterId = from;
      if (this.pc) { try { this.pc.close(); } catch {} }
      const pc = new RTCPeerConnection(ICE);
      this.pc = pc;
      pc.ontrack = (ev) => this.onTrack(ev.streams[0]);
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') this.onStatus('receiving');
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') this.onStatus('reconnecting');
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) this._send({ type: 'ice', to: from, candidate: e.candidate });
      };
      await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
      const ans = await pc.createAnswer();
      await pc.setLocalDescription(ans);
      this._send({ type: 'answer', to: from, sdp: ans.sdp });
      this.onStatus('receiving');
    } else if (type === 'ice' && this.pc && msg.candidate) {
      try { await this.pc.addIceCandidate(msg.candidate); } catch {}
    }
  }

  _send(m) { try { this.ws.send(JSON.stringify(m)); } catch {} }
  _startPing() { this._stopPing(); this._ping = setInterval(() => this._send({ type: 'ping' }), 20000); }
  _stopPing() { if (this._ping) { clearInterval(this._ping); this._ping = null; } }

  stop() {
    this.stopped = true;
    this._stopPing();
    try { this.pc?.close(); } catch {}
    try { this.ws?.close(); } catch {}
  }
}
